import React, { useContext } from "react"

import { Form, Formik } from "formik"
import * as Yup from "yup"

import { gql, useClient, useMutation } from "hooks/urql"

import {
  CheckboxField,
  Field,
  FormFailures,
  SubmitButton,
  handleFailure,
} from "components/Forms/Formik/hookComponents"

import RegistrationContext from "./RegistrationContext"

const ValidationSchema = Yup.object().shape({
  // parent fields
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().required("Email is required"),
  phoneNumber: Yup.string().required("Phone number is required"),
  zipCode: Yup.string()
    .required("Zip code is required")
    .length(5, "Zip code must be 5 characters"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  passwordConfirmation: Yup.string()
    .required("Please confirm your password")
    .equals([Yup.ref("password")], "Passwords do not match"),
  termsAccepted: Yup.boolean().oneOf(
    [true],
    "You must accept the Air Tutors privacy policy"
  ),
})

type ParentFormProps = {
  reset: () => void
}

const ParentForm: React.FC<ParentFormProps> = ({ reset }) => {
  const { referral } = useContext(RegistrationContext)
  const shouldUseReferralData = referral?.userType === "parent"
  const client = useClient()
  const [, register]: any[] = useMutation(registerMutation, client)

  const formState = {
    referralId: referral?.id,
    firstName: (shouldUseReferralData && referral?.firstName) || "",
    lastName: (shouldUseReferralData && referral?.lastName) || "",
    email: (shouldUseReferralData && referral?.email) || "",
    phoneNumber: (shouldUseReferralData && referral?.phoneNumber) || "",
    zipCode: "",
    password: "",
    passwordConfirmation: "",
    termsAccepted: false,
  }

  const handleSubmit = (values, actions) => {
    register({ input: { ...values } })
      .then(result => {
        if (result.error) {
          handleFailure(actions, [result.error])
          return
        }
        const { failures, redirectTo } = result.data.parentRegistration
        if (failures) {
          handleFailure(actions, failures)
        } else {
          window.location.href = redirectTo
        }
      })
      .catch(e => {
        if (e.message) {
          handleFailure(actions, [e])
        } else {
          handleFailure(actions)
        }
      })
  }

  return (
    <div className="rounded-md border-2 border-solid border-gray-200 bg-gray-50 p-5">
      <h2 className="mt-0">Parent Profile</h2>
      <Formik
        initialValues={formState}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
      >
        <Form>
          <Field name="firstName" label="First Name" autoFocus />
          <Field name="lastName" label="Last Name" />
          <Field name="email" label="Email" autoComplete="email" />
          <Field
            name="phoneNumber"
            label="Phone Number"
            autoComplete="phoneNumber"
          />
          <Field name="zipCode" label="Zip Code" />
          <Field
            name="password"
            type="password"
            label="Password"
            autoComplete="new-password"
          />
          <Field
            name="passwordConfirmation"
            type="password"
            label="Password Confirmation"
            autoComplete="new-password"
          />
          <CheckboxField
            name="termsAccepted"
            label={
              <>
                I am giving Air Tutors permission to contact my family regarding
                setting up tutoring services and agree to the&nbsp;
                <a href="/privacy-policy" target="_blank">
                  Air Tutors privacy policy
                </a>
              </>
            }
          />

          <div className="mt-3 flex flex-col items-center justify-between space-y-2 sm:flex-row sm:space-y-0">
            <button type="button" onClick={reset} className="btn red">
              Back
            </button>
            <SubmitButton text="Register" className="btn solid green" />
          </div>
          <FormFailures />
        </Form>
      </Formik>
    </div>
  )
}

const registerMutation = gql`
  mutation ($input: ParentRegistrationInputObject!) {
    parentRegistration(input: $input) {
      failures {
        message
      }
      redirectTo
    }
  }
`

export default ParentForm
